import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import inventoryService, { ProductFilters, CreateProductDto, InventoryStats } from '../../services/inventoryService';
import { Product } from '../../types';

interface InventoryState {
  list:      Product[];
  lowStock:  Product[];
  stats:     InventoryStats | null;
  selected:  Product | null;
  loading:   boolean;
  error:     string | null;
}

const initialState: InventoryState = {
  list: [], lowStock: [], stats: null, selected: null, loading: false, error: null,
};

const upsert = (list: Product[], p: Product) => {
  const i = list.findIndex(x => x._id === p._id);
  if (i !== -1) list[i] = p; else list.unshift(p);
};

export const fetchInventoryStats = createAsyncThunk('inventory/stats',    async (_, { rejectWithValue }) => { try { return await inventoryService.getStats(); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const fetchProducts       = createAsyncThunk('inventory/fetchAll', async (f: ProductFilters, { rejectWithValue }) => { try { return await inventoryService.getProducts(f); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const fetchLowStock       = createAsyncThunk('inventory/lowStock', async (_, { rejectWithValue }) => { try { return await inventoryService.getLowStock(); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const createProduct       = createAsyncThunk('inventory/create',   async (d: CreateProductDto, { rejectWithValue }) => { try { return await inventoryService.create(d); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const updateProduct       = createAsyncThunk('inventory/update',   async ({ id, data }: { id: string; data: Partial<CreateProductDto> }, { rejectWithValue }) => { try { return await inventoryService.update(id, data); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const deactivateProduct   = createAsyncThunk('inventory/deactivate',async (id: string, { rejectWithValue }) => { try { await inventoryService.deactivate(id); return id; } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const adjustStock         = createAsyncThunk('inventory/adjust',   async ({ id, quantity, reason }: { id: string; quantity: number; reason?: string }, { rejectWithValue }) => { try { return await inventoryService.adjust(id, quantity, reason); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });

const inventorySlice = createSlice({
  name: 'inventory', initialState,
  reducers: {
    setSelected: (state, action: PayloadAction<Product | null>) => { state.selected = action.payload; },
    clearError:  (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending,     (s) => { s.loading = true; s.error = null; })
      .addCase(fetchProducts.fulfilled,   (s, a) => { s.loading = false; s.list = a.payload.data ?? []; })
      .addCase(fetchProducts.rejected,    (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(fetchInventoryStats.fulfilled, (s, a) => { s.stats = a.payload.data ?? null; })
      .addCase(fetchLowStock.fulfilled,   (s, a) => { s.lowStock = a.payload.data ?? []; })
      .addCase(createProduct.fulfilled,   (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(updateProduct.fulfilled,   (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); if (s.selected?._id === a.payload.data._id) s.selected = a.payload.data; } })
      .addCase(deactivateProduct.fulfilled,(s, a) => { const p = s.list.find(p => p._id === a.payload); if (p) p.isActive = false; if (s.selected?._id === a.payload) s.selected.isActive = false; })
      .addCase(adjustStock.fulfilled,     (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); if (s.selected?._id === a.payload.data._id) s.selected = a.payload.data; } });
  },
});

export const { setSelected, clearError } = inventorySlice.actions;
export default inventorySlice.reducer;
